import { makeStyles } from "tss-react/mui"
import { alertClasses, iconClasses, buttonClasses } from "@mui/material"
import { lightBlue, lightGreen, orange, red, indigo } from "../../core/palette"

export const useStyles = makeStyles()(
  ({ spacing, typography, shadows, palette, breakpoints }) => ({
    base: {
      // Always lay on top regardless of the other elements (Needed for login page to sit on top of the image)
      position: "relative",
      zIndex: 100,

      display: "flex",
      alignItems: "flex-start",
      borderRadius: "3px",

      [`& > .${alertClasses.message}`]: {
        width: "100%",
        display: "flex",
        gap: spacing(1),
        padding: 0
      },

      [`& > .${alertClasses.icon}`]: {
        padding: 0,
        opacity: 1,

        [`& > .${iconClasses.root}`]: {
          display: "flex",
          alignItems: "center",
          fontSize: typography.body1.fontSize,
          height: typography.body1.lineHeight
        }
      },

      [`& > .${alertClasses.action}`]: {
        marginRight: 0,
        padding: 0,

        [`& .${buttonClasses.root}`]: {
          color: lightBlue[700],
          backgroundColor: "transparent",

          "&:hover": {
            backgroundColor: lightBlue[50]
          }
        }
      }
    },
    lg: {
      padding: spacing(4, 3, 4, 5),
      fontSize: typography.body1[breakpoints.up("md")]["fontSize"],
      lineHeight: typography.body1[breakpoints.up("md")]["lineHeight"],

      [`& > .${alertClasses.icon}`]: {
        marginRight: spacing(5),

        [`& > .${iconClasses.root}`]: {
          fontSize: typography.pxToRem(16),
          height: typography.body1[breakpoints.up("md")]["lineHeight"]
        }
      },

      [`& > .${alertClasses.action}`]: {
        marginTop: "-5px",
        marginBottom: "-5px"
      }
    },
    md: {
      padding: spacing(3, 2, 3, 4),
      fontSize: typography.body2[breakpoints.up("md")]["fontSize"],
      lineHeight: typography.body2[breakpoints.up("md")]["lineHeight"],

      [`& > .${alertClasses.icon}`]: {
        marginRight: spacing(3),

        [`& > .${iconClasses.root}`]: {
          fontSize: typography.pxToRem(14),
          height: typography.body2[breakpoints.up("md")]["lineHeight"]
        }
      },

      [`& > .${alertClasses.action}`]: {
        marginTop: "-6px",
        marginBottom: "-6px"
      }
    },
    sm: {
      padding: spacing(3, 3, 3, 2),
      fontSize: typography.caption[breakpoints.up("md")]["fontSize"],
      lineHeight: typography.caption[breakpoints.up("md")]["lineHeight"],

      [`& > .${alertClasses.icon}`]: {
        marginRight: spacing(2),

        [`& > .${iconClasses.root}`]: {
          fontSize: typography.pxToRem(12),
          height: typography.caption[breakpoints.up("md")]["lineHeight"]
        }
      },

      [`& > .${alertClasses.action}`]: {
        marginTop: "-8px",
        marginBottom: "-8px"
      }
    },
    dark_success: {
      backgroundColor: lightGreen[700],
      color: palette.common.white,
      boxShadow: shadows[6],

      [`& > .${alertClasses.icon}`]: {
        color: palette.common.white
      },

      [`& > .${alertClasses.action}`]: {
        [`& .${buttonClasses.root}`]: {
          color: palette.common.white,
          backgroundColor: "transparent",

          "&:hover": {
            backgroundColor: lightGreen[800]
          }
        }
      }
    },
    dark_warning: {
      backgroundColor: orange[700],
      color: palette.common.white,
      boxShadow: shadows[6],

      [`& > .${alertClasses.icon}`]: {
        color: palette.common.white
      },

      [`& > .${alertClasses.action}`]: {
        [`& .${buttonClasses.root}`]: {
          color: palette.common.white,
          backgroundColor: "transparent",

          "&:hover": {
            backgroundColor: orange[800]
          }
        }
      }
    },
    dark_error: {
      backgroundColor: red[600],
      color: palette.common.white,
      boxShadow: shadows[6],

      [`& > .${alertClasses.icon}`]: {
        color: palette.common.white
      },

      [`& > .${alertClasses.action}`]: {
        [`& .${buttonClasses.root}`]: {
          color: palette.common.white,
          backgroundColor: "transparent",

          "&:hover": {
            backgroundColor: red[500]
          }
        }
      }
    },
    dark_info: {
      backgroundColor: lightBlue[600],
      color: palette.common.white,
      boxShadow: shadows[6],

      [`& > .${alertClasses.icon}`]: {
        color: palette.common.white
      },

      [`& > .${alertClasses.action}`]: {
        [`& .${buttonClasses.root}`]: {
          color: palette.common.white,
          backgroundColor: "transparent",

          "&:hover": {
            backgroundColor: lightBlue[500]
          }
        }
      }
    },
    light_success: {
      backgroundColor: lightGreen[100],
      color: lightGreen[900],

      [`& > .${alertClasses.icon}`]: {
        color: lightGreen[700]
      }
    },
    light_warning: {
      backgroundColor: orange[100],
      color: orange[950],

      [`& > .${alertClasses.icon}`]: {
        color: orange[700]
      }
    },
    light_error: {
      backgroundColor: red[100],
      color: red[800],

      [`& > .${alertClasses.icon}`]: {
        color: red[600]
      }
    },
    light_info: {
      backgroundColor: lightBlue[100],
      color: lightBlue[800],

      [`& > .${alertClasses.icon}`]: {
        color: lightBlue[600]
      }
    },
    outlined_success: {
      backgroundColor: palette.common.white,
      outline: `1px solid ${lightGreen[900]}`,
      color: lightGreen[900],

      [`& > .${alertClasses.icon}`]: {
        color: lightGreen[700]
      }
    },
    outlined_warning: {
      backgroundColor: palette.common.white,
      outline: `1px solid ${orange[900]}`,
      color: orange[950],

      [`& > .${alertClasses.icon}`]: {
        color: orange[700]
      }
    },
    outlined_error: {
      backgroundColor: palette.common.white,
      outline: `1px solid ${red[600]}`,
      color: red[800],

      [`& > .${alertClasses.icon}`]: {
        color: red[600]
      }
    },
    outlined_info: {
      backgroundColor: palette.common.white,
      outline: `1px solid ${lightBlue[600]}`,
      color: lightBlue[800],

      [`& > .${alertClasses.icon}`]: {
        color: lightBlue[600]
      }
    },
    text: {
      backgroundColor: palette.common.white,
      border: "none"
    },
    text_success: {
      color: lightGreen[900],

      [`& > .${alertClasses.icon}`]: {
        color: lightGreen[700]
      }
    },
    text_warning: {
      color: orange[950],

      [`& > .${alertClasses.icon}`]: {
        color: orange[700]
      }
    },
    text_error: {
      color: red[800],

      [`& > .${alertClasses.icon}`]: {
        color: red[600]
      }
    },
    text_info: {
      color: lightBlue[800],

      [`& > .${alertClasses.icon}`]: {
        color: lightBlue[600]
      }
    },
    dark_persistent: {
      backgroundColor: indigo[600],
      color: palette.common.white,
      boxShadow: shadows[6],
      padding: spacing(4, 8),

      [`& > .${alertClasses.icon}`]: {
        color: palette.common.white
      },

      [`& > .${alertClasses.action}`]: {
        [`& .${buttonClasses.root}`]: {
          color: palette.common.white,
          backgroundColor: indigo[700],

          "&:hover": {
            backgroundColor: indigo[800]
          }
        }
      }
    },
    light_persistent: {
      backgroundColor: indigo[100],
      color: indigo[900],
      padding: "17px 32px",

      [`& > .${alertClasses.icon}`]: {
        color: palette.common.white
      },

      [`& > .${alertClasses.action}`]: {
        [`& .${buttonClasses.root}`]: {
          color: palette.common.white,
          backgroundColor: indigo[200],

          "&:hover": {
            backgroundColor: indigo[400]
          }
        }
      }
    },
    outlined_persistent: {
      backgroundColor: palette.common.white,
      outline: `1px solid ${indigo[900]}`,
      color: indigo[900],
      padding: "17px 32px",

      [`& > .${alertClasses.icon}`]: {
        color: indigo[700]
      }
    },
    text_persistent: {
      color: indigo[800],

      [`& > .${alertClasses.icon}`]: {
        color: indigo[600]
      }
    }
  })
)
